import React, { useState, useEffect } from "react";
import authFetch, { setAuthToken } from "../../authFetch";
import { isNative, registerPushNotifications } from "../../native";
import T from "../../i18n/translations";
import { useValidationToast, validateFields } from "../../utils/validation";
import { anchorDerivedColors } from "../../utils/color";
import { buildDarkAccentCss, buildLightAccentCss } from "../../utils/themeAccent";

/* ══ LOGIN ═════════════════════════════════════════════════ */
export default function Login({users,onLogin,lang,setLang,appName,tagline,branding,dispatch,syncError,wsKey}){
  const vt=useValidationToast();
  const [email,setEmail]=useState("");const [pw,setPw]=useState("");const [remember,setRemember]=useState(false);const [err,setErr]=useState("");
  const [submitting,setSubmitting]=useState(false);
  // Forgot-password flow: null | "email" | "code" | "newpw"
  const [fpStep,setFpStep]=useState(null);
  const [fpEmail,setFpEmail]=useState("");const [fpCode,setFpCode]=useState("");const [fpNewPw,setFpNewPw]=useState("");
  const [fpErr,setFpErr]=useState("");const [fpLoading,setFpLoading]=useState(false);
  const t=T[lang];
  useEffect(()=>{try{const r=localStorage.getItem("tros_remember");if(r){const d=JSON.parse(r);setEmail(d.e||"");setRemember(true);}}catch{}},[]);

  const sendFpCode=async()=>{
    /* Check user exists — try in-memory first, then DB */
    let u=users.find(u=>u.email?.toLowerCase()===fpEmail.trim().toLowerCase()&&u.active);
    if(!u){
      try{
        const r=await authFetch(`/api/crud?table=workspace_users&ws_key=${encodeURIComponent(wsKey)}`);
        const d=await r.json();
        u=(d.rows||[]).find(u=>u.email?.toLowerCase()===fpEmail.trim().toLowerCase()&&u.active);
      }catch{}
    }
    if(!u){setFpErr("No active user found with that email.");return;}
    setFpLoading(true);setFpErr("");
    try{
      const r=await authFetch("/api/send-verification",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({email:fpEmail.trim()})});
      const d=await r.json();
      if(d.ok){setFpStep("code");}else{setFpErr(d.error||"Failed to send code.");}
    }catch{setFpErr("Network error.");}
    setFpLoading(false);
  };
  const verifyFpCode=async()=>{
    setFpLoading(true);setFpErr("");
    try{
      const r=await authFetch("/api/send-verification",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({email:fpEmail.trim(),code:fpCode.trim()})});
      const d=await r.json();
      if(d.ok&&d.valid){setFpStep("newpw");}else{setFpErr(d.reason||"Invalid code.");}
    }catch{setFpErr("Network error.");}
    setFpLoading(false);
  };
  const saveFpPassword=async()=>{
    const m=validateFields([{ok:fpNewPw&&fpNewPw.length>=6,label:isRTL?"كلمة مرور (6 أحرف)":"Password (min 6 chars)"}]);
    if(m.length){vt.show(m.join(", "));setFpErr("Password must be at least 6 characters.");return;}
    /* Find user ID for the password change */
    let u=users.find(u=>u.email?.toLowerCase()===fpEmail.trim().toLowerCase()&&u.active);
    if(!u){
      try{
        const r=await authFetch(`/api/crud?table=workspace_users&ws_key=${encodeURIComponent(wsKey)}`);
        const d=await r.json();
        u=(d.rows||[]).find(u=>u.email?.toLowerCase()===fpEmail.trim().toLowerCase()&&u.active);
      }catch{}
    }
    if(!u){setFpErr("User not found.");return;}
    setFpLoading(true);
    try{
      const r=await authFetch("/api/auth",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({action:"change_password",ws_key:wsKey,user_id:u.id,new_password:fpNewPw})});
      const d=await r.json();
      if(!d.ok){setFpErr(d.error||"Failed to save password.");setFpLoading(false);return;}
    }catch{setFpErr("Network error.");setFpLoading(false);return;}
    setFpLoading(false);
    setFpStep(null);setFpEmail("");setFpCode("");setFpNewPw("");setFpErr("");
    setEmail(u.email);
  };

  const submit=async()=>{
    const m=validateFields([{ok:email.trim(),label:isRTL?"البريد":"Email"},{ok:pw,label:isRTL?"كلمة المرور":"Password"}]);
    if(m.length){vt.show(m.join(", "));return;}
    setSubmitting(true);setErr("");
    try{
      const r=await authFetch("/api/auth",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({action:"login",ws_key:wsKey,email:email.trim(),password:pw})});
      const d=await r.json();
      if(!r.ok){
        if(r.status===403) setErr(lang==="ar"?"تم تعليق حسابك. يرجى التواصل مع مالك مساحة العمل للاستفسار.":"Your account has been suspended. Please contact your workspace owner for information.");
        else setErr(lang==="ar"?"البريد الإلكتروني أو كلمة المرور غير صحيحة":d.error||"Incorrect email or password.");
        setSubmitting(false);return;
      }
      if(d.token)setAuthToken(d.token);
      if(remember)try{localStorage.setItem("tros_remember",JSON.stringify({e:email}));}catch{}
      else try{localStorage.removeItem("tros_remember");}catch{}
      /* Register push notifications on native after successful login */
      if(isNative()){
        registerPushNotifications(
          (token)=>{try{authFetch("/api/crud",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({table:"push_tokens",action:"upsert",ws_key:wsKey,data:{user_id:d.user?.id,token,platform:navigator.userAgent.includes("Android")?"android":"ios"}})});}catch{}},
          null
        );
      }
      setSubmitting(false);
      onLogin(d.user);
    }catch{
      setErr("Network error — please try again.");
      setSubmitting(false);
    }
  };
  const isRTL=lang==="ar";

  /* ── Derive theme + colours from workspace branding ── */
  const br=branding||{};
  const isDark=br.themeMode==="dark"||(br.themeMode==="auto"&&typeof window!=="undefined"&&window.matchMedia("(prefers-color-scheme: dark)").matches);
  const primary=anchorDerivedColors(br.anchorColor).primary;
  const accent =br.anchorColor||"#C9A84C";
  /* Hex → "R,G,B" for rgba() usage */
  const h2r=hex=>{const r=parseInt(hex.slice(1,3),16),g=parseInt(hex.slice(3,5),16),b=parseInt(hex.slice(5,7),16);return`${r},${g},${b}`;};
  const pRgb=h2r(primary.length===7?primary:"#1A1714");
  const aRgb=h2r(accent.length===7?accent:"#C9A84C");
  /* Slightly lighter accent for button gradient */
  const accentLight=accent; /* keep same — subtle single-colour gradient */
  const logo=isDark?(br.logoDark||br.logoLight):(br.logoLight||br.logoDark);

  /* ── Per-mode dynamic accent CSS (reuse landing-page helpers) ── */
  const darkAccentCss=isDark?buildDarkAccentCss(accent):"";
  const lightAccentCss=!isDark?buildLightAccentCss(accent):"";
  /* Override a few workspace-specific tweaks on top of platform classes */
  const wsLoginOverrides=isDark?`
.ws-login .p-inp-dark:focus{border-color:rgba(${aRgb},.55);box-shadow:0 0 0 3px rgba(${aRgb},.07);}
.ws-login .p-btn-primary-dark{background:linear-gradient(135deg,${accent},${accent}cc);}
.ws-login .p-btn-primary-dark:hover{box-shadow:0 12px 36px rgba(${aRgb},.3);}
.ws-login .p-lang-btn-dark.active{background:${accent};color:#08080f;border-color:${accent};}
.ws-login .ws-login-forgot{color:rgba(${aRgb},.65);background:none;border:none;font-size:12px;cursor:pointer;padding:0;transition:color .12s;}
.ws-login .ws-login-forgot:hover{color:rgba(${aRgb},.9);}
.ws-login .ws-login-rem-box.on{background:${accent};border-color:${accent};}
.ws-login .ws-login-back{color:rgba(${aRgb},.65);cursor:pointer;font-size:13px;transition:color .12s;}
.ws-login .ws-login-back:hover{color:rgba(${aRgb},.9);}
`:`
.ws-login .p-inp-light:focus{border-color:rgba(${aRgb},.55);box-shadow:0 0 0 3px rgba(${aRgb},.08);}
.ws-login .p-btn-primary-light{background:${accent};color:white;}
.ws-login .p-btn-primary-light:hover{box-shadow:0 12px 36px rgba(${aRgb},.25);}
.ws-login .p-lang-btn-light.active{background:${accent};color:white;border-color:${accent};}
.ws-login .ws-login-forgot{color:${accent};background:none;border:none;font-size:12px;cursor:pointer;padding:0;transition:color .12s;}
.ws-login .ws-login-forgot:hover{opacity:.8;}
.ws-login .ws-login-rem-box.on{background:${accent};border-color:${accent};}
.ws-login .ws-login-back{color:${accent};cursor:pointer;font-size:13px;transition:color .12s;}
.ws-login .ws-login-back:hover{opacity:.8;}
`;

  /* ── Dark theme render ── */
  if(isDark){
    return(
      <div className={`p-dark ws-login${isRTL?" rtl":""}`} style={{fontFamily:isRTL?"'Tajawal',-apple-system,sans-serif":"'Inter',-apple-system,sans-serif"}}>
        {vt.Toast}

        {darkAccentCss&&<style>{darkAccentCss}</style>}
        <style>{wsLoginOverrides}</style>
        <div className="p-dark-blob"/>
        <div className="p-hero-grid"/>
        <nav className="p-nav-dark">
          <div className="p-logo-dark">
            {logo
              ?<img src={logo} style={{height:28,objectFit:"contain"}} alt={appName||"TR OS"}/>
              :<span>{(appName||"TR OS").split(" ")[0]}{(appName||"TR OS").split(" ").length>1&&<span className="p-logo-dark-accent"> {(appName||"TR OS").split(" ").slice(1).join(" ")}</span>}</span>
            }
          </div>
          <div style={{display:"flex",gap:3,alignItems:"center"}}>
            <button className={`p-lang-btn-dark ${lang==="en"?"active":""}`} style={{padding:"8px 10px",fontSize:13}} onClick={()=>setLang("en")}>EN</button>
            <button className={`p-lang-btn-dark ${lang==="ar"?"active":""}`} style={{padding:"8px 10px",fontSize:13}} onClick={()=>setLang("ar")}>ع</button>
          </div>
        </nav>
        <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",padding:"76px 24px 24px",position:"relative",zIndex:1,minHeight:"100vh",boxSizing:"border-box"}}>
          <div style={{maxWidth:440,width:"100%"}}>
            {/* ── Forgot-password flow ── */}
            {fpStep==="email"&&(
              <>
                <div style={{marginBottom:28}}>
                  <h2 style={{fontFamily:"'Outfit','Inter',sans-serif",fontSize:24,fontWeight:600,color:"white",letterSpacing:"-.02em",marginBottom:6}}>{isRTL?"إعادة تعيين كلمة المرور":"Reset Password"}</h2>
                  <p style={{fontSize:14,color:"rgba(255,255,255,.42)"}}>{isRTL?"أدخل بريدك لإرسال رمز التحقق":"Enter your email to receive a reset code"}</p>
                </div>
                {fpErr&&<div className="p-err-dark" style={{marginBottom:14}}>{fpErr}</div>}
                <div className="p-fg-dark"><label className="p-lbl-dark">{t.email}</label><input className="p-inp-dark" type="email" value={fpEmail} onChange={e=>{setFpEmail(e.target.value);setFpErr("");}} placeholder="your@email.com" autoFocus onKeyDown={e=>e.key==="Enter"&&sendFpCode()}/></div>
                <button className="p-btn-primary-dark" onClick={sendFpCode} disabled={fpLoading} style={{width:"100%",padding:"14px",fontSize:15,borderRadius:12,marginTop:4}}>{fpLoading?(isRTL?"جاري الإرسال…":"Sending…"):(isRTL?"إرسال الرمز":"Send Code")} →</button>
                <div style={{textAlign:"center",marginTop:16}}><span className="ws-login-back" onClick={()=>{setFpStep(null);setFpErr("");}}>← {isRTL?"رجوع":"Back to sign in"}</span></div>
              </>
            )}
            {fpStep==="code"&&(
              <>
                <div style={{marginBottom:28}}>
                  <h2 style={{fontFamily:"'Outfit','Inter',sans-serif",fontSize:24,fontWeight:600,color:"white",letterSpacing:"-.02em",marginBottom:6}}>{isRTL?"أدخل الرمز":"Enter Code"}</h2>
                  <p style={{fontSize:14,color:"rgba(255,255,255,.42)"}}>{isRTL?`أرسلنا رمزاً إلى ${fpEmail}`:`We sent a 6-digit code to ${fpEmail}`}</p>
                </div>
                {fpErr&&<div className="p-err-dark" style={{marginBottom:14}}>{fpErr}</div>}
                <div className="p-fg-dark"><label className="p-lbl-dark">{isRTL?"رمز التحقق":"Verification Code"}</label><input className="p-inp-dark" type="text" inputMode="numeric" maxLength={6} value={fpCode} onChange={e=>{setFpCode(e.target.value.replace(/\D/g,""));setFpErr("");}} placeholder="_ _ _ _ _ _" style={{letterSpacing:"0.3em",textAlign:"center",fontSize:22}} autoFocus onKeyDown={e=>e.key==="Enter"&&verifyFpCode()}/></div>
                <button className="p-btn-primary-dark" onClick={verifyFpCode} disabled={fpLoading} style={{width:"100%",padding:"14px",fontSize:15,borderRadius:12,marginTop:4}}>{fpLoading?(isRTL?"جاري التحقق…":"Verifying…"):(isRTL?"تحقق":"Verify")} →</button>
                <div style={{textAlign:"center",marginTop:16}}><span className="ws-login-back" onClick={()=>{setFpStep("email");setFpCode("");setFpErr("");}}>← {isRTL?"أعد الإرسال":"Resend code"}</span></div>
              </>
            )}
            {fpStep==="newpw"&&(
              <>
                <div style={{marginBottom:28}}>
                  <h2 style={{fontFamily:"'Outfit','Inter',sans-serif",fontSize:24,fontWeight:600,color:"white",letterSpacing:"-.02em",marginBottom:6}}>{isRTL?"كلمة مرور جديدة":"New Password"}</h2>
                  <p style={{fontSize:14,color:"rgba(255,255,255,.42)"}}>{isRTL?"اختر كلمة مرور جديدة لحسابك":"Choose a new password for your account"}</p>
                </div>
                {fpErr&&<div className="p-err-dark" style={{marginBottom:14}}>{fpErr}</div>}
                <div className="p-fg-dark"><label className="p-lbl-dark">{isRTL?"كلمة المرور الجديدة":"New Password"}</label><input className="p-inp-dark" type="password" value={fpNewPw} onChange={e=>{setFpNewPw(e.target.value);setFpErr("");}} placeholder={isRTL?"6 أحرف على الأقل":"Min 6 characters"} autoFocus onKeyDown={e=>e.key==="Enter"&&saveFpPassword()}/></div>
                <button className="p-btn-primary-dark" onClick={saveFpPassword} style={{width:"100%",padding:"14px",fontSize:15,borderRadius:12,marginTop:4}}>{isRTL?"حفظ":"Save Password"} →</button>
              </>
            )}
            {/* ── Normal sign-in ── */}
            {!fpStep&&(
              <>
                <div style={{marginBottom:28}}>
                  <h2 style={{fontFamily:"'Outfit','Inter',sans-serif",fontSize:24,fontWeight:600,color:"white",letterSpacing:"-.02em",marginBottom:6}}>{t.signIn}</h2>
                  <p style={{fontSize:14,color:"rgba(255,255,255,.42)"}}>{tagline||(isRTL?"نظام المكتب الخاص":"Private Office System")}</p>
                </div>
                {err&&<div className="p-err-dark" style={{marginBottom:14}}>{err}</div>}
                <div className="p-fg-dark"><label className="p-lbl-dark">{t.email}</label><input className="p-inp-dark" type="email" value={email} onChange={e=>{setEmail(e.target.value);setErr("");}} placeholder="your@email.com" autoFocus onKeyDown={e=>e.key==="Enter"&&submit()}/></div>
                <div className="p-fg-dark"><label className="p-lbl-dark">{t.password}</label><input className="p-inp-dark" type="password" value={pw} onChange={e=>{setPw(e.target.value);setErr("");}} placeholder="••••••••" onKeyDown={e=>e.key==="Enter"&&submit()}/></div>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer"}} onClick={()=>setRemember(r=>!r)}>
                    <div className={`ws-login-rem-box${remember?" on":""}`} style={{width:16,height:16,border:"1.5px solid rgba(255,255,255,.2)",borderRadius:4,display:"flex",alignItems:"center",justifyContent:"center",transition:"all .12s",flexShrink:0}}>{remember&&<span style={{color:"#08080f",fontSize:10,fontWeight:700,lineHeight:1}}>✓</span>}</div>
                    <span style={{fontSize:13,color:"rgba(255,255,255,.38)",userSelect:"none"}}>{t.rememberMe}</span>
                  </div>
                  <button className="ws-login-forgot" onClick={()=>{setFpStep("email");setFpEmail(email);setFpErr("");}}>{t.forgotPassword}</button>
                </div>
                <button className="p-btn-primary-dark" onClick={submit} disabled={submitting} style={{width:"100%",padding:"14px",fontSize:15,borderRadius:12,marginTop:4,opacity:submitting?0.6:1}}>{submitting?"…":t.signIn+" →"}</button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  /* ── Light theme render ── */
  return(
    <div className={`p-light ws-login${isRTL?" rtl":""}`} style={{minHeight:"100vh",fontFamily:isRTL?"'Tajawal',-apple-system,sans-serif":"'Inter',-apple-system,sans-serif"}}>
      {vt.Toast}

      {lightAccentCss&&<style>{lightAccentCss}</style>}
      <style>{wsLoginOverrides}</style>
      <div className="p-light-blob"/>
      <div className="p-hero-grid"/>
      <nav className="p-nav-light">
        <div className="p-logo-light">
          {logo
            ?<img src={logo} style={{height:28,objectFit:"contain"}} alt={appName||"TR OS"}/>
            :<span>{(appName||"TR OS").split(" ")[0]}{(appName||"TR OS").split(" ").length>1&&<span className="p-logo-light-accent"> {(appName||"TR OS").split(" ").slice(1).join(" ")}</span>}</span>
          }
        </div>
        <div style={{display:"flex",gap:3,alignItems:"center"}}>
          <button className={`p-lang-btn-light ${lang==="en"?"active":""}`} style={{padding:"8px 10px",fontSize:13}} onClick={()=>setLang("en")}>EN</button>
          <button className={`p-lang-btn-light ${lang==="ar"?"active":""}`} style={{padding:"8px 10px",fontSize:13}} onClick={()=>setLang("ar")}>ع</button>
        </div>
      </nav>
      <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",padding:"76px 24px 24px",position:"relative",zIndex:1,minHeight:"100vh",boxSizing:"border-box"}}>
        <div style={{maxWidth:440,width:"100%"}}>
          {/* ── Forgot-password flow ── */}
          {fpStep==="email"&&(
            <>
              <div style={{marginBottom:28}}>
                <h2 style={{fontFamily:"'Outfit','Inter',sans-serif",fontSize:24,fontWeight:600,color:primary,letterSpacing:"-.02em",marginBottom:6}}>{isRTL?"إعادة تعيين كلمة المرور":"Reset Password"}</h2>
                <p style={{fontSize:14,color:"#6B6660"}}>{isRTL?"أدخل بريدك لإرسال رمز التحقق":"Enter your email to receive a reset code"}</p>
              </div>
              {fpErr&&<div className="p-err-light" style={{marginBottom:14}}>{fpErr}</div>}
              <div className="p-fg-light"><label className="p-lbl-light">{t.email}</label><input className="p-inp-light" type="email" value={fpEmail} onChange={e=>{setFpEmail(e.target.value);setFpErr("");}} placeholder="your@email.com" autoFocus onKeyDown={e=>e.key==="Enter"&&sendFpCode()}/></div>
              <button className="p-btn-primary-light" onClick={sendFpCode} disabled={fpLoading} style={{width:"100%",padding:"14px",fontSize:15,borderRadius:12,marginTop:4}}>{fpLoading?(isRTL?"جاري الإرسال…":"Sending…"):(isRTL?"إرسال الرمز":"Send Code")} →</button>
              <div style={{textAlign:"center",marginTop:16}}><span className="ws-login-back" onClick={()=>{setFpStep(null);setFpErr("");}}>← {isRTL?"رجوع":"Back to sign in"}</span></div>
            </>
          )}
          {fpStep==="code"&&(
            <>
              <div style={{marginBottom:28}}>
                <h2 style={{fontFamily:"'Outfit','Inter',sans-serif",fontSize:24,fontWeight:600,color:primary,letterSpacing:"-.02em",marginBottom:6}}>{isRTL?"أدخل الرمز":"Enter Code"}</h2>
                <p style={{fontSize:14,color:"#6B6660"}}>{isRTL?`أرسلنا رمزاً إلى ${fpEmail}`:`We sent a 6-digit code to ${fpEmail}`}</p>
              </div>
              {fpErr&&<div className="p-err-light" style={{marginBottom:14}}>{fpErr}</div>}
              <div className="p-fg-light"><label className="p-lbl-light">{isRTL?"رمز التحقق":"Verification Code"}</label><input className="p-inp-light" type="text" inputMode="numeric" maxLength={6} value={fpCode} onChange={e=>{setFpCode(e.target.value.replace(/\D/g,""));setFpErr("");}} placeholder="_ _ _ _ _ _" style={{letterSpacing:"0.3em",textAlign:"center",fontSize:22}} autoFocus onKeyDown={e=>e.key==="Enter"&&verifyFpCode()}/></div>
              <button className="p-btn-primary-light" onClick={verifyFpCode} disabled={fpLoading} style={{width:"100%",padding:"14px",fontSize:15,borderRadius:12,marginTop:4}}>{fpLoading?(isRTL?"جاري التحقق…":"Verifying…"):(isRTL?"تحقق":"Verify")} →</button>
              <div style={{textAlign:"center",marginTop:16}}><span className="ws-login-back" onClick={()=>{setFpStep("email");setFpCode("");setFpErr("");}}>← {isRTL?"أعد الإرسال":"Resend code"}</span></div>
            </>
          )}
          {fpStep==="newpw"&&(
            <>
              <div style={{marginBottom:28}}>
                <h2 style={{fontFamily:"'Outfit','Inter',sans-serif",fontSize:24,fontWeight:600,color:primary,letterSpacing:"-.02em",marginBottom:6}}>{isRTL?"كلمة مرور جديدة":"New Password"}</h2>
                <p style={{fontSize:14,color:"#6B6660"}}>{isRTL?"اختر كلمة مرور جديدة لحسابك":"Choose a new password for your account"}</p>
              </div>
              {fpErr&&<div className="p-err-light" style={{marginBottom:14}}>{fpErr}</div>}
              <div className="p-fg-light"><label className="p-lbl-light">{isRTL?"كلمة المرور الجديدة":"New Password"}</label><input className="p-inp-light" type="password" value={fpNewPw} onChange={e=>{setFpNewPw(e.target.value);setFpErr("");}} placeholder={isRTL?"6 أحرف على الأقل":"Min 6 characters"} autoFocus onKeyDown={e=>e.key==="Enter"&&saveFpPassword()}/></div>
              <button className="p-btn-primary-light" onClick={saveFpPassword} style={{width:"100%",padding:"14px",fontSize:15,borderRadius:12,marginTop:4}}>{isRTL?"حفظ":"Save Password"} →</button>
            </>
          )}
          {/* ── Normal sign-in ── */}
          {!fpStep&&(
            <>
              <div style={{marginBottom:28}}>
                <h2 style={{fontFamily:"'Outfit','Inter',sans-serif",fontSize:24,fontWeight:600,color:primary,letterSpacing:"-.02em",marginBottom:6}}>{t.signIn}</h2>
                <p style={{fontSize:14,color:"#6B6660"}}>{tagline||(isRTL?"نظام المكتب الخاص":"Private Office System")}</p>
              </div>
              {err&&<div className="p-err-light" style={{marginBottom:14}}>{err}</div>}
              <div className="p-fg-light"><label className="p-lbl-light">{t.email}</label><input className="p-inp-light" type="email" value={email} onChange={e=>{setEmail(e.target.value);setErr("");}} placeholder="your@email.com" autoFocus onKeyDown={e=>e.key==="Enter"&&submit()}/></div>
              <div className="p-fg-light"><label className="p-lbl-light">{t.password}</label><input className="p-inp-light" type="password" value={pw} onChange={e=>{setPw(e.target.value);setErr("");}} placeholder="••••••••" onKeyDown={e=>e.key==="Enter"&&submit()}/></div>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
                <div style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer"}} onClick={()=>setRemember(r=>!r)}>
                  <div className={`ws-login-rem-box${remember?" on":""}`} style={{width:16,height:16,border:`1.5px solid rgba(${pRgb},.22)`,borderRadius:4,display:"flex",alignItems:"center",justifyContent:"center",transition:"all .12s",flexShrink:0}}>{remember&&<span style={{color:primary,fontSize:10,fontWeight:700,lineHeight:1}}>✓</span>}</div>
                  <span style={{fontSize:13,color:`rgba(${pRgb},.48)`,userSelect:"none"}}>{t.rememberMe}</span>
                </div>
                <button className="ws-login-forgot" onClick={()=>{setFpStep("email");setFpEmail(email);setFpErr("");}}>{t.forgotPassword}</button>
              </div>
              <button className="p-btn-primary-light" onClick={submit} disabled={submitting} style={{width:"100%",padding:"14px",fontSize:15,borderRadius:12,marginTop:4,opacity:submitting?0.6:1}}>{submitting?"…":t.signIn+" →"}</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
